import { convertTimeStringToSeconds, fetchMediaTailorResponse } from '../MediatailorSessionUtil';
import {
  AnyFunc,
  AsyncUtil,
  createError,
  createEventEmitter,
  ErrorCodes,
  eventObject,
  Logger,
  Error,
  PlatformUtil,
} from '@quickplay/foundation';
import {
  AdContentTimePosition,
  AdEvent,
  Player,
  PlayerContext,
  AdInfo,
  AdBreakInfo,
  PLAYBACK_STATES,
  AdPlaybackPolicy,
  PlaybackFastForwardMode,
  PlaybackRewindMode,
  PlaybackInterruptedMode,
  PlaybackAutoSeekMode,
  PlaybackRepeatedMode,
  SEEKING_STATES,
  MediaType,
} from '@quickplay/player-interface';
import { MediatailorErrorCodes } from '../MediatailorErrorCodes';
import {
  Ad,
  AdWithIndex,
  Avail,
  AvailWithIndex,
  MediatailorAdHandlerMetadata,
  MediaTailorTrackingResponse,
  TrackingEvent,
} from '../../model/AdMetadata';
import { MediatailorPlaybackPolicyHandler } from '../policy/AdPlaybackPolicyHandler';
import { getSeekPositionFor, getTotalAdvertsBreaksDuration } from '../policy/AdPolicyUtil';
import { MediatailorAdHandler } from '../../model/MediatailorAdHandler';
import { AdQuartileTracker } from '../AdQuartileTracker';

export class MediatailorAdHandlerImpl implements MediatailorAdHandler {
  private static readonly TIME_THRESHOLD = 0.001;
  private static readonly TIME_VALUE_UNKNOWN = -1.0;
  private static readonly PROCESS_ADS_INTERVAL_MS = 250;
  private static readonly APPLE_MEDIATAILOR_POLL_INTERVAL_MS = 6_000;
  private static readonly PREVIOUS_PLAYHEAD_INITIAL_VALUE = -2;
  private static readonly CURRENT_PLAYHEAD_INITIAL_VALUE = -1;
  private readonly player_: Player;
  private readonly trackingUrl_: string;
  private readonly logger_?: Logger;
  private readonly eventEmitter = createEventEmitter();
  private readonly mediatailorPollIntervalInMS: number;
  private adMetadata_?: MediaTailorTrackingResponse;
  private avails: Avail[] = [];
  private playerContext_: PlayerContext = PlayerContext.MAIN;
  private currentAvail_?: Avail;
  private currentAd_?: Ad;
  private currentAdBreakInfo_?: AdBreakInfo;
  private currentAdInfo?: AdInfo;
  private previousAdBreakInfo_?: AdBreakInfo;
  private previousPlayhead = MediatailorAdHandlerImpl.PREVIOUS_PLAYHEAD_INITIAL_VALUE;
  private currentTime_ = MediatailorAdHandlerImpl.CURRENT_PLAYHEAD_INITIAL_VALUE;
  private shouldReportAdProgress = false;
  private processAdsInterval?: any;
  private trackingPollInterval?: any;
  private readonly quartileTracker: AdQuartileTracker;

  /**
   * Tracks the timestamp (in ms) of the last successful {@link fetchAdMetadata} call triggered
   * by a `manifestupdated` event. Used by {@link isManifestFetchAllowed_} to throttle rapid
   * bursts of manifest updates (e.g. during buffering) and prevent the tracking API from being
   * hit more frequently than {@link mediatailorPollIntervalInMS}.
   */
  private lastManifestFetchTimeMs_ = 0;
  private adPolicyHandler: MediatailorPlaybackPolicyHandler | undefined = undefined;
  private connectionTimeoutMS?: number;
  private maxRetries?: number;
  private playerLoadStartTimeInMS = 0;
  private prerollLoadTimeInMS = 0;
  private defaultAdPlaybackPolicy: AdPlaybackPolicy = {
    fastForwardMode: PlaybackFastForwardMode.TRICK_FW_PLAY_LAST,
    rewindMode: PlaybackRewindMode.TRICK_RW_SKIP_ALL,
    autoSeekMode: PlaybackAutoSeekMode.AUTO_SEEK_SKIP_ALL,
    repeatMode: PlaybackRepeatedMode.REPEAT_PLAY_ONCE,
    interruptMode: PlaybackInterruptedMode.INTERRUPT_RESUME,
  };

  constructor(player: Player, mediatailorAdHandlerMetadata: MediatailorAdHandlerMetadata, logger: Logger) {
    this.player_ = player;
    this.trackingUrl_ = mediatailorAdHandlerMetadata.trackingUrl;
    this.logger_ = logger;
    this.quartileTracker = new AdQuartileTracker(this.eventEmitter);
    this.adMetadata_ = mediatailorAdHandlerMetadata?.adMetadata;
    if (this.adMetadata_) {
      this.updateAdMetadata(this.adMetadata_);
    }
    this.adPolicyHandler = new MediatailorPlaybackPolicyHandler(
      player,
      mediatailorAdHandlerMetadata.adPlaybackPolicy ?? this.defaultAdPlaybackPolicy,
      logger,
    );
    this.connectionTimeoutMS = mediatailorAdHandlerMetadata.connectionTimeoutMS;
    this.maxRetries = mediatailorAdHandlerMetadata.maxRetries;
    // Use provided interval or default to 10 seconds
    this.mediatailorPollIntervalInMS =
      mediatailorAdHandlerMetadata.hlsAdTrackingPollIntervalMS ??
      MediatailorAdHandlerImpl.APPLE_MEDIATAILOR_POLL_INTERVAL_MS;
    this.initialize();
  }

  subscribe(eventName: string | eventObject, callback?: AnyFunc): void {
    this.eventEmitter.subscribe(eventName, callback);
  }

  unsubscribe(eventName: string | eventObject, callback?: AnyFunc): void {
    this.eventEmitter.unsubscribe(eventName, callback);
  }

  adClicked(adUrl: string): void {
    this.eventEmitter.publish(AdEvent.ADVERT_CLICK, adUrl);
  }

  getAllAdBreaks(): AdBreakInfo[] {
    return this.avails.map((avail, index) => this.createAdBreakInfo(avail, index));
  }

  currentTime(): number {
    if (!this.player_) {
      return 0;
    }
    return this.player_.isLive
      ? this.player_.currentTime
      : (this.adPolicyHandler?.getCurrentTime(this.currentAvail_) ?? this.player_.currentTime);
  }

  duration(): number {
    if (!this.player_ || !this.player_.duration || isNaN(this.player_.duration)) {
      return 0;
    }
    if (this.player_.isLive) {
      return this.player_.duration;
    }
    return this.player_.duration - getTotalAdvertsBreaksDuration(this.avails ?? []);
  }

  seek(seekPoint: number): void {
    if (!this.player_) {
      return;
    }

    if (!this.adPolicyHandler) {
      this.player_.seek(seekPoint);
      return;
    }
    this.player_.seek(
      this.adPolicyHandler.willSeekTo(getSeekPositionFor(seekPoint, this.avails ?? [], this.player_.duration)) ?? 0,
    );
  }

  getInitialPlaybackTimeWithAds(): number {
    if (
      !this.player_ ||
      this.player_.isLive ||
      !this.player_.playbackOptions ||
      !this.player_.playbackOptions.initialPlaybackTime
    ) {
      return 0;
    }
    const position = this.player_.playbackOptions.initialPlaybackTime;
    let initialPlaybackTimeWithAds = getSeekPositionFor(position, this.avails ?? [], this.player_.duration);
    initialPlaybackTimeWithAds =
      this.adPolicyHandler?.willSeekTo(initialPlaybackTimeWithAds, true) ?? initialPlaybackTimeWithAds;
    const isInitialPlaybackPositionMatchingAnyAdbreakStartTime = this.avails.some(
      (adBreak: Avail) => adBreak.startTimeInSeconds === initialPlaybackTimeWithAds,
    );
    if (isInitialPlaybackPositionMatchingAnyAdbreakStartTime) {
      initialPlaybackTimeWithAds -= MediatailorAdHandlerImpl.TIME_THRESHOLD;
    }
    return initialPlaybackTimeWithAds;
  }

  getCuePointsRelativeToContentTime(): number[] {
    if (!this.player_) {
      this.logger_?.warn('[AD] Player is not available for cue point calculation.');
      return [];
    }

    if (this.avails.length === 0) {
      this.logger_?.warn('[AD] No ad breaks available to calculate cue points.');
      return [];
    }

    const watchedAdBreaks = this.adPolicyHandler?.getWatchedAdBreaks() ?? [];
    const watchedAdBreakIds = new Set(watchedAdBreaks.map(avail => avail.availId));

    const unwatchedAvails = this.avails.filter(avail => {
      return !watchedAdBreakIds.has(avail.availId);
    });

    if (unwatchedAvails.length === 0) {
      return [];
    }

    const cuePoints = unwatchedAvails.map(adBreak => {
      const totalAdTime = this.avails
        .slice(0, this.avails.indexOf(adBreak))
        .reduce((sum, avail) => sum + (avail?.durationInSeconds ?? 0), 0);

      const relativeContentTime = (adBreak.startTimeInSeconds ?? 0) - totalAdTime;

      if ((adBreak?.startTimeInSeconds ?? 0) + (adBreak?.durationInSeconds ?? 0) === this.player_.duration) {
        return adBreak.startTimeInSeconds ?? 0;
      }

      return Math.max(relativeContentTime, 0);
    });

    return [...new Set(cuePoints)];
  }

  playerContext(): PlayerContext {
    return this.playerContext_;
  }

  getPrerollLoadTimeInMS(): number | undefined {
    return this.prerollLoadTimeInMS;
  }

  private async initialize(): Promise<void> {
    try {
      this.subscribeToPlayerEvents();
    } catch (error) {
      this.logger_?.error('[AD] Failed to initialize MediaTailor handler:', error);
    }
  }

  private async fetchAdMetadata(): Promise<void> {
    const shouldFetchTrackingDataForVOD = this.player_?.isLive || !this.adMetadata_?.avails?.length;
    if (!this.trackingUrl_ || !shouldFetchTrackingDataForVOD) {
      return;
    }

    this.removeOutdatedAvails();

    try {
      const metadata = await await AsyncUtil.retryOnNetworkFailure(
        () =>
          fetchMediaTailorResponse<MediaTailorTrackingResponse>(this.trackingUrl_, undefined, this.connectionTimeoutMS),
        this.maxRetries,
      );

      if (this.hasAdDataChanged(metadata)) {
        this.updateAdMetadata(metadata);
      }
    } catch (error) {
      this.handleFetchError(error);
    }
  }

  private hasAdDataChanged(newMetadata: MediaTailorTrackingResponse): boolean {
    if (!this.adMetadata_?.avails || !newMetadata?.avails) {
      return true;
    }
    return JSON.stringify(newMetadata.avails) !== JSON.stringify(this.adMetadata_.avails);
  }

  private updateAdMetadata(metadata: MediaTailorTrackingResponse): void {
    this.adMetadata_ = metadata;
    let avails = metadata?.avails;
    if (!avails) {
      return;
    }

    // Filter out avails with invalid duration
    avails = avails.filter(
      avail => avail.durationInSeconds !== undefined && avail.durationInSeconds !== null && avail.durationInSeconds > 0,
    );

    const newAvailIds = new Set(avails.map(avail => avail.availId));

    const remainingAvails = this.avails.filter(avail => !newAvailIds.has(avail.availId));

    this.avails = [...remainingAvails, ...avails];
    this.updateCurrentAdBreakInfoIfNeeded(avails);
  }

  private updateCurrentAdBreakInfoIfNeeded(updatedAvails: Avail[]): void {
    if (this.playerContext_ !== PlayerContext.AD || !this.currentAdBreakInfo_) {
      return;
    }

    const currentAdBreakId = this.currentAdBreakInfo_.adBreakID;
    const updatedAvail = updatedAvails.find(avail => avail.availId === currentAdBreakId);

    if (updatedAvail) {
      const availIndex = this.avails.findIndex(avail => avail.availId === currentAdBreakId);
      this.currentAdBreakInfo_ = this.createAdBreakInfo(updatedAvail, availIndex >= 0 ? availIndex : 0);
      if (this.currentAd_) {
        const currentAdIndex = updatedAvail.ads?.findIndex(ad => ad.adId === this.currentAd_?.adId) ?? -1;
        if (currentAdIndex >= 0 && updatedAvail.ads) {
          const updatedAd = updatedAvail.ads[currentAdIndex];
          this.currentAd_ = updatedAd;
          this.currentAdInfo = this.createAdInfo(updatedAd, currentAdIndex);
        }
      }
    }
  }

  private removeOutdatedAvails(): void {
    if (!this.player_?.rawPlayer?.seekRange) {
      return;
    }

    const seekableRangeStart = this.player_.rawPlayer.seekRange().start;

    this.avails = this.avails.filter(adBreak => {
      const startTime = adBreak.startTimeInSeconds ?? 0;
      const endTime = startTime + (adBreak.durationInSeconds ?? 0);
      // 1 & 2. Filter adbreak startTime which lies within seekableRange
      // 3. Filter adbreak which is currently active. (Note: This condition is added when user switched to other tab
      // where ad get paused due to browser policy. On returning back, ad metadata processor which is running periodically will remove the
      // currently playing adbreakInfo since it goes seekableRange boundary)
      return (
        startTime >= seekableRangeStart ||
        endTime >= seekableRangeStart ||
        this.currentAvail_?.availId === adBreak.availId
      );
    });
  }

  private handleFetchError(error: any): void {
    this.logger_?.error(
      '[AD] Failed to fetch ad metadata:',
      createError(
        MediatailorErrorCodes.TRACKING_API_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
        'Failed to fetch ad metadata',
        error,
      ),
    );
  }

  private evaluateAdEvents(): void {
    if (!this.hasValidState()) {
      return;
    }

    const currentTime = this.currentTime_;
    const previousTime = this.previousPlayhead;

    const currentAvailResult = this.findAdBreak(currentTime);
    const previousAvailResult = this.findAdBreak(previousTime);
    const currentAdResult = currentAvailResult ? this.findAd(currentTime) : null;
    const previousAdResult = previousAvailResult ? this.findAd(previousTime) : null;
    this.evaluateAllAdEvents(currentAvailResult, previousAvailResult, currentAdResult, previousAdResult);
  }

  private evaluateAllAdEvents(
    currentAvailResult: AvailWithIndex | null,
    previousAvailResult: AvailWithIndex | null,
    currentAdResult: AdWithIndex | null,
    previousAdResult: AdWithIndex | null,
  ): void {
    const currentAvail = currentAvailResult?.avail;
    const previousAvail = previousAvailResult?.avail;
    const enteringAdBreak = this.isEnteringAdBreak(currentAvail, previousAvail);
    const transitioningBetweenAdBreaks = this.isTransitioningBetweenAdBreaks(currentAvail, previousAvail);

    if (
      currentAvail &&
      this.adPolicyHandler &&
      (enteringAdBreak || transitioningBetweenAdBreaks) &&
      this.adPolicyHandler.playbackRepeatedRule(currentAvail) !== true
    ) {
      this.discardAdBreak(currentAvail);
      return;
    }

    if (enteringAdBreak) {
      this.handleEnteringAdBreak(currentAvailResult, currentAdResult);
    } else if (this.isLeavingAdBreak(currentAvail, previousAvail)) {
      this.handleLeavingAdBreak(previousAvailResult, previousAdResult);
    } else if (transitioningBetweenAdBreaks) {
      this.handleAdBreakTransition(currentAvailResult, previousAvailResult, currentAdResult, previousAdResult);
    } else if (this.isWithinSameAdBreak(currentAvail, previousAvail)) {
      this.handleAdTransitionsWithinBreak(currentAdResult, previousAdResult);
    }
  }

  private isEnteringAdBreak(currentAvail: Avail | undefined, previousAvail: Avail | undefined): boolean {
    /**
     * Entering ad break when current avail (current state) is present but previous avail (previous state) is not present.
     * This also covers the scenario when player enters into an ad break directly without going back to content in between (e.g. due to seeking or when there are back-to-back ad breaks).
     */
    return !!(currentAvail && (!previousAvail || !this.shouldReportAdProgress));
  }

  private isLeavingAdBreak(currentAvail: Avail | undefined, previousAvail: Avail | undefined): boolean {
    /**
     * Leaving ad break when current avail (current state) is not present but previous avail (previous state) is present.
     * This also covers the scenario when player leaves an ad break directly without going back to content in between (e.g. due to seeking or when there are back-to-back ad breaks).
     */
    return !!(!currentAvail && previousAvail && this.shouldReportAdProgress);
  }

  private isTransitioningBetweenAdBreaks(currentAvail: Avail | undefined, previousAvail: Avail | undefined): boolean {
    /**
     * If there are subsequent ad breaks with no content in between, player can transition from one ad break to another without going back to content.
     * In this case, both current avail and previous avail are present but they are different.
     */
    return !!(currentAvail && previousAvail && currentAvail.availId !== previousAvail.availId);
  }

  private isWithinSameAdBreak(currentAvail: Avail | undefined, previousAvail: Avail | undefined): boolean {
    /**
     * If the current avail and previous avail are the same, the player is within the same ad break.
     */
    return !!(currentAvail && previousAvail && currentAvail.availId === previousAvail.availId);
  }

  private handleEnteringAdBreak(currentAvailResult: AvailWithIndex | null, currentAdResult: AdWithIndex | null): void {
    if (!currentAvailResult) return;

    this.handleAdBreakStart(currentAvailResult.avail, currentAvailResult.index);
    if (currentAdResult) {
      this.handleAdStart(currentAdResult.ad, currentAdResult.index);
    }
  }

  private handleLeavingAdBreak(previousAvailResult: AvailWithIndex | null, previousAdResult: AdWithIndex | null): void {
    if (!previousAvailResult) return;

    if (previousAdResult) {
      this.handleAdEnd(previousAdResult.ad, previousAdResult.index);
    }
    this.handleAdBreakEnd(previousAvailResult.avail, previousAvailResult.index);
  }

  private handleAdBreakTransition(
    currentAvailResult: AvailWithIndex | null,
    previousAvailResult: AvailWithIndex | null,
    currentAdResult: AdWithIndex | null,
    previousAdResult: AdWithIndex | null,
  ): void {
    if (previousAdResult) {
      this.handleAdEnd(previousAdResult.ad, previousAdResult.index);
    }
    if (previousAvailResult) {
      this.handleAdBreakEnd(previousAvailResult.avail, previousAvailResult.index);
    }
    if (currentAvailResult) {
      this.handleAdBreakStart(currentAvailResult.avail, currentAvailResult.index);
    }
    if (currentAdResult) {
      this.handleAdStart(currentAdResult.ad, currentAdResult.index);
    }
  }

  private handleAdTransitionsWithinBreak(
    currentAdResult: AdWithIndex | null,
    previousAdResult: AdWithIndex | null,
  ): void {
    const currentAd = currentAdResult?.ad;
    const previousAd = previousAdResult?.ad;

    if (previousAd && (!currentAd || previousAd.adId !== currentAd?.adId)) {
      this.handleAdEnd(previousAd, previousAdResult?.index ?? 0);
    }

    if (currentAd && (!previousAd || currentAd.adId !== previousAd?.adId)) {
      this.handleAdStart(currentAd, currentAdResult?.index ?? 0);
    }
  }

  private hasValidState(): boolean {
    return !!(this.player_ && this.avails && this.avails.length > 0);
  }

  private findAdBreak(playhead: number): AvailWithIndex | null {
    for (let index = 0; index < this.avails.length; index++) {
      const avail = this.avails[index];
      if (this.isPlayheadInAdBreak(playhead, avail)) {
        return { avail, index };
      }
    }
    return null;
  }

  private findAd(playhead: number): AdWithIndex | null {
    for (const avail of this.avails) {
      if (this.isPlayheadInAdBreak(playhead, avail) && avail.ads) {
        return this.findAdInAvail(playhead, avail, avail.ads);
      }
    }
    return null;
  }

  private isPlayheadInAdBreak(playhead: number, avail: Avail): boolean {
    const startTime = avail.startTimeInSeconds;
    const duration = avail.durationInSeconds;

    if (startTime === undefined || startTime === null || !duration) {
      return false;
    }

    return this.isTimeInRange(playhead, startTime, startTime + duration);
  }

  private findAdInAvail(playhead: number, avail: Avail, ads: Ad[]): AdWithIndex | null {
    if (avail.startTimeInSeconds === undefined || avail.startTimeInSeconds === null) {
      return null;
    }

    for (let index = 0; index < ads.length; index++) {
      const adStart = ads[index].startTimeInSeconds;
      const ad = ads[index];
      const adEnd = ad.startTimeInSeconds + (ad.durationInSeconds ?? 0);

      if (this.isTimeInRange(playhead, adStart, adEnd)) {
        return { ad, index };
      }
    }

    return null;
  }

  private isTimeInRange(time: number, start: number, end: number): boolean {
    return this.compare(start, time) && time < end;
  }

  private compare(first: number, second: number): boolean {
    return first <= second || Math.abs(first - second) < MediatailorAdHandlerImpl.TIME_THRESHOLD;
  }

  private handleAdBreakStart(adBreak: Avail, availIndex: number): void {
    this.logger_?.log('[AD] Ad break started:', {
      adBreakId: adBreak.availId,
      index: availIndex,
    });

    this.playerContext_ = PlayerContext.AD;
    this.currentAvail_ = adBreak;
    this.currentAdBreakInfo_ = this.createAdBreakInfo(adBreak, availIndex);
    this.eventEmitter.publish('playercontextchanged', this.playerContext_);

    // Computation of preroll ad load time.
    // Note: Check for undefined explicitly as startTimeInSeconds can be 0 for preroll ads
    if (this.currentAvail_.startTimeInSeconds !== undefined && this.currentAvail_.startTimeInSeconds === 0) {
      this.prerollLoadTimeInMS = Number((performance.now() - this.playerLoadStartTimeInMS).toFixed(2));
    }
    this.eventEmitter.publish(AdEvent.ADVERT_BREAK_START, this.currentAdBreakInfo_);
  }

  private discardAdBreak(adBreak: Avail): void {
    if (this.adPolicyHandler && typeof this.adPolicyHandler.discardAdBreak === 'function') {
      this.adPolicyHandler.discardAdBreak(adBreak);
      return;
    }

    if (adBreak.startTimeInSeconds !== undefined && adBreak.durationInSeconds !== undefined) {
      this.player_.seek(adBreak.startTimeInSeconds + adBreak.durationInSeconds);
    }
  }

  private handleAdBreakEnd(adBreak: Avail, availIndex: number): void {
    this.logger_?.log('[AD] Ad break ended:', { adBreakId: adBreak.availId });
    this.playerContext_ = PlayerContext.MAIN;
    if (!this.player_?.isLive) {
      this.adPolicyHandler?.setWatchedAdBreak(adBreak);
      this.player_ && this.adPolicyHandler?.resolveSeekIntent(this.player_.currentTime);
      this.eventEmitter.publish(AdEvent.ADVERT_CUEPOINTS, this.getCuePointsRelativeToContentTime());
    }
    this.currentAvail_ = undefined;
    this.previousAdBreakInfo_ = this.createAdBreakInfo(adBreak, availIndex);
    this.eventEmitter.publish('playercontextchanged', this.playerContext_);
    this.eventEmitter.publish(AdEvent.ADVERT_BREAK_END, this.previousAdBreakInfo_);
  }

  private handleAdStart(ad: Ad, adIndex: number): void {
    this.logger_?.log('[AD] Ad started:', {
      adId: ad.adId,
      index: adIndex,
    });

    this.shouldReportAdProgress = true;
    this.currentAd_ = ad;
    this.currentAdInfo = this.createAdInfo(ad, adIndex);
    this.quartileTracker.reset();

    this.eventEmitter.publish(AdEvent.ADVERT_START, this.currentAdInfo);
  }

  private handleAdEnd(ad: Ad, adIndex: number): void {
    this.logger_?.log('[AD] Ad ended:', {
      adId: ad.adId,
      index: adIndex,
    });

    this.shouldReportAdProgress = false;
    this.currentAd_ = undefined;

    const adInfo = this.createAdInfo(ad, adIndex);
    this.eventEmitter.publish(AdEvent.ADVERT_END, adInfo);
  }

  private createAdBreakInfo(avail: Avail, index: number): AdBreakInfo {
    return {
      adBreakID: avail.availId ?? '',
      contentTimePosition: this.player_?.isLive
        ? AdContentTimePosition.MIDROLL
        : this.getAdPosition(avail?.startTimeInSeconds ?? 0, avail?.durationInSeconds ?? 0),
      adBreakStartTimeOffsetMs: (avail?.startTimeInSeconds ?? 0) * 1000,
      durationMs: (avail?.durationInSeconds ?? 0) * 1000,
      totalAds: avail?.ads?.length ?? 0,
      remainingTimeMs: MediatailorAdHandlerImpl.TIME_VALUE_UNKNOWN,
      adSequencePosition: MediatailorAdHandlerImpl.TIME_VALUE_UNKNOWN,
      // Note: Check explicitly for 0 as it's a valid value for preroll ads
      adBreakIndex: avail.startTimeInSeconds !== undefined && avail.startTimeInSeconds === 0 ? 0 : index,
    };
  }

  private getAdClickThroughURL(ad: Ad): string | undefined {
    if (!ad.trackingEvents || !Array.isArray(ad.trackingEvents)) {
      return undefined;
    }
    const clickThroughEvent = ad.trackingEvents.find(
      (trackingEvent: TrackingEvent) => trackingEvent.eventType === 'clickThrough',
    );
    return clickThroughEvent?.beaconUrls?.[0];
  }

  private getAdClickTrackingURLs(ad: Ad): string[] {
    if (!ad.trackingEvents || !Array.isArray(ad.trackingEvents)) {
      return [];
    }
    const clickTrackingEvent = ad.trackingEvents.find(
      (trackingEvent: TrackingEvent) => trackingEvent.eventType === 'clickTracking',
    );
    return clickTrackingEvent?.beaconUrls ?? [];
  }

  private createAdInfo(ad: Ad, adIndex: number): AdInfo {
    return {
      adID: ad.adId,
      sequence: adIndex + 1,
      adStartTimeOffsetMs: (ad.startTimeInSeconds ?? 0) * 1000,
      durationMs: (ad.durationInSeconds ?? 0) * 1000,
      isSkippable: !!ad.skipOffset,
      skipOffsetMs: ad.skipOffset
        ? convertTimeStringToSeconds(ad.skipOffset)
        : MediatailorAdHandlerImpl.TIME_VALUE_UNKNOWN,
      isFiller: false,
      remainingTimeMs: this.calculateRemainingTimeMs(ad),
      adBreakInfo: this.currentAdBreakInfo_,
      vastProperties: {
        videoClickThroughURL: this.getAdClickThroughURL(ad),
        videoClickTrackingURLs: this.getAdClickTrackingURLs(ad),
        adTitle: ad.adTitle,
        creativeId: ad.creativeId,
        adSystem: ad.adSystem,
      },
      delivery: { technology: 'server-side', stitcher: 'AWS MediaTailor' },
    };
  }

  private calculateRemainingTimeMs(ad: Ad): number {
    if (
      !this.player_ ||
      ad.startTimeInSeconds === undefined ||
      ad.startTimeInSeconds === null ||
      ad.durationInSeconds === undefined ||
      ad.durationInSeconds === null
    ) {
      return MediatailorAdHandlerImpl.TIME_VALUE_UNKNOWN;
    }

    const adStartTime = ad.startTimeInSeconds;
    const adEndTime = adStartTime + ad.durationInSeconds;
    const currentTime = this.player_.currentTime;

    if (currentTime <= adEndTime) {
      return (adEndTime - currentTime) * 1000;
    }

    return 0;
  }

  private getAdPosition(startTime: number, adDuration: number): AdContentTimePosition {
    const contentDuration = this.player_?.duration;

    if (startTime === 0) return AdContentTimePosition.PREROLL;

    if (contentDuration !== undefined && startTime + adDuration === contentDuration) {
      return AdContentTimePosition.POSTROLL;
    }

    return AdContentTimePosition.MIDROLL;
  }

  private processAds = (): void => {
    if (!this.player_) {
      return;
    }
    this.currentTime_ = this.player_.currentTime || MediatailorAdHandlerImpl.CURRENT_PLAYHEAD_INITIAL_VALUE;
    this.reportAdProgress();
    this.evaluateAdEvents();
    this.previousPlayhead = this.currentTime_;
  };

  private reportAdProgress(): void {
    if (!this.currentAd_ || !this.shouldReportAdProgress || !this.currentAdInfo) {
      return;
    }

    const adStartTime = this.currentAd_.startTimeInSeconds ?? 0;
    const adDuration = this.currentAd_.durationInSeconds ?? 0;
    const progress = this.currentTime_ - adStartTime;

    this.currentAdInfo.remainingTimeMs = this.calculateRemainingTimeMs(this.currentAd_);

    this.quartileTracker.emitQuartileEvents(progress, adDuration);

    this.eventEmitter.publish(AdEvent.ADVERT_PROGRESS_UPDATE, this.currentAdInfo, progress);
  }

  private onManifestUpdated = (): void => {
    // For live streams, we want to fetch the latest ad metadata on manifest updates to ensure we have the most up-to-date ad break information.
    // However, for VOD content, we only want to fetch ad metadata once during initialization since the ad breaks are not expected to change.
    // Additionally, for Apple platforms, we rely on periodic polling to fetch ad metadata updates since manifest update events may not be triggered reliably.
    // Therefore, we only fetch ad metadata on manifest updates for live streams on non-Apple platforms to avoid unnecessary network requests and potential performance issues.
    // During buffering, the player may fire manifestupdated in rapid bursts. isManifestFetchAllowed_ throttles these to at most one fetch per mediatailorPollIntervalInMS.
    const shouldAllowFetchingAdMetadata = this.player_?.isLive && this.isManifestFetchAllowed_();
    if (!shouldAllowFetchingAdMetadata) return;

    this.fetchAdMetadata().catch(error => {
      this.logger_?.error('[AD] Failed to update metadata on manifest change:', error);
    });
  };

  /**
   * Throttle guard for {@link onManifestUpdated}.
   *
   * During buffering, Shaka fires `manifestupdated` in rapid bursts — sometimes 30+ times
   * in quick succession — which would cause the MediaTailor tracking API to be hit an
   * equivalent number of times. This method enforces a minimum interval of
   * {@link mediatailorPollIntervalInMS} between consecutive {@link fetchAdMetadata} calls
   * originating from manifest update events.
   *
   * On the first call (or once the interval has elapsed) it records the current timestamp
   * in {@link lastManifestFetchTimeMs_} and returns `true`, allowing the fetch to proceed.
   * Subsequent calls within the throttle window return `false` and are silently skipped.
   *
   * @returns `true` if enough time has passed since the last fetch and the call should proceed;
   *          `false` if the call is within the throttle window and should be suppressed.
   */
  private readonly isHlsPlayback_ = (): boolean =>
    this.player_?.rawPlayer?.getManifestType?.()?.toLowerCase() === MediaType.HLS;

  private readonly isManifestFetchAllowed_ = (): boolean => {
    if (!this.isHlsPlayback_()) {
      // Throttle is only needed for Apple platforms where manifestupdated events can be unreliable and may fire in bursts during buffering. For non-Apple platforms, allow all manifest updates to trigger metadata fetches.
      return true;
    }
    const now = Date.now();
    if (now - this.lastManifestFetchTimeMs_ < this.mediatailorPollIntervalInMS) {
      this.logger_?.log('[AD] Skipping manifest update fetch — throttled.');
      return false;
    }
    this.lastManifestFetchTimeMs_ = now;
    return true;
  };

  private onPlaybackStateChanged = (playbackState: PLAYBACK_STATES): void => {
    switch (playbackState) {
      case PLAYBACK_STATES.LOADING:
        this.playerLoadStartTimeInMS = performance.now();
        break;
      case PLAYBACK_STATES.STARTED:
        this.startProcessing();
        break;
      case PLAYBACK_STATES.PAUSED:
        this.stopProcessing();
        break;
      case PLAYBACK_STATES.STOPPED:
      case PLAYBACK_STATES.IDLE:
        this.reset();
        break;
    }
  };

  private initializeWithInitialPlaybackTime(): void {
    if (
      !this.player_ ||
      this.player_.isLive ||
      !this.player_.playbackOptions ||
      !this.player_.playbackOptions.initialPlaybackTime
    ) {
      return;
    }

    const initialPlaybackTimeWithAds = this.getInitialPlaybackTimeWithAds();

    this.logger_?.log(`[MEDIATAILOR_ADS] overriding initial playback time`, initialPlaybackTimeWithAds);

    this.player_.seek(initialPlaybackTimeWithAds);
  }
  private onStreamEnded = (): void => {
    this.stopProcessing();
    this.previousPlayhead = MediatailorAdHandlerImpl.PREVIOUS_PLAYHEAD_INITIAL_VALUE;
    this.currentTime_ = MediatailorAdHandlerImpl.CURRENT_PLAYHEAD_INITIAL_VALUE;
    this.playerContext_ = PlayerContext.MAIN;
    this.eventEmitter.publish('playercontextchanged', this.playerContext_);
  };

  private onManifestParsed = async (): Promise<void> => {
    this.player_?.unsubscribe('progressupdate', this.onProgressUpdate);
    await this.fetchAdMetadata();
    if (this.player_?.isLive) {
      this.adPolicyHandler = undefined;
      return;
    }
    this.adPolicyHandler?.setAdBreaks(this.avails);
    this.initializeWithInitialPlaybackTime();
    this.eventEmitter.publish(AdEvent.ADVERT_CUEPOINTS, this.getCuePointsRelativeToContentTime());
  };

  private onSeekingStateChanged = (seekState: SEEKING_STATES): void => {
    if (this.player_.playerState !== PLAYBACK_STATES.STARTED) {
      return;
    }
    if (seekState === SEEKING_STATES.ACTIVE) {
      this.stopProcessing();
    } else {
      this.startProcessing();
    }
  };

  private readonly onError = (error: Error): void => {
    if (this.playerContext_ !== PlayerContext.AD) {
      return;
    }
    this.eventEmitter.publish(AdEvent.ADVERT_ERROR, error, this.currentAdInfo);
  };

  private startProcessing(): void {
    if (!this.processAdsInterval) {
      this.processAdsInterval = setInterval(this.processAds, MediatailorAdHandlerImpl.PROCESS_ADS_INTERVAL_MS);
    }
    // Start tracking poll timer only for Apple platforms
    if (PlatformUtil.isApple()) {
      this.startTrackingPoll();
    }
  }

  private stopProcessing(): void {
    if (this.processAdsInterval) {
      clearInterval(this.processAdsInterval);
      this.processAdsInterval = undefined;
    }
    // Stop tracking poll timer for Apple platforms
    if (PlatformUtil.isApple()) {
      this.stopTrackingPoll();
    }
  }

  /**
   * Starts periodic tracking API polling for Apple platforms.
   * This is required because Apple platforms don't always trigger manifestupdated events reliably.
   */
  private startTrackingPoll(): void {
    if (!this.trackingPollInterval && this.player_?.isLive) {
      this.logger_?.log('[AD] Starting tracking poll timer for Apple platform');
      this.trackingPollInterval = setInterval(() => {
        this.fetchAdMetadata().catch(error => {
          this.logger_?.error('[AD] Failed to fetch ad metadata in tracking poll:', error);
        });
      }, this.mediatailorPollIntervalInMS);
    }
  }

  /**
   * Stops periodic tracking API polling for Apple platforms.
   */
  private stopTrackingPoll(): void {
    if (this.trackingPollInterval) {
      this.logger_?.log('[AD] Stopping tracking poll timer for Apple platform');
      clearInterval(this.trackingPollInterval);
      this.trackingPollInterval = undefined;
    }
  }

  private reset(): void {
    this.stopProcessing();
    this.stopTrackingPoll();
    this.resetState();
    this.unsubscribeToPlayerEvents();
  }

  private resetState(): void {
    this.currentAd_ = undefined;
    this.currentAdBreakInfo_ = undefined;
    this.currentAdInfo = undefined;
    this.playerContext_ = PlayerContext.MAIN;
    this.eventEmitter.publish('playercontextchanged', this.playerContext_);
  }

  private readonly onProgressUpdate = (): void => {
    this.onManifestParsed();
  };

  private subscribeToPlayerEvents(): void {
    if (!this.player_) {
      return;
    }

    const baseEvents = [
      { event: 'playbackstatechanged', handler: this.onPlaybackStateChanged },
      { event: 'streamended', handler: this.onStreamEnded },
      { event: 'manifestparsed', handler: this.onManifestParsed },
      { event: 'seekingstatechanged', handler: this.onSeekingStateChanged },
      { event: 'error', handler: this.onError },
    ];

    baseEvents.forEach(({ event, handler }) => this.player_.subscribe(event, handler));

    /**
     * During validation on continue watching scenario on Apple platforms, it was observed that
     * seeking to initial playback time with ads was not working as expected if seek happens before the content is completely loaded.
     * This is similar issue that observed on seeking to initial playback time for live restart streams on Apple platforms.
     * To handle this properly, the onProgressUpdate handler is added to ensure seeking to initial playback time with ads
     * is handled correctly on continue watching scenarios.
     */
    // HLS tracking data is only available once the variant playlist is requested, so on any platform the fetch after
    // the master manifest can be empty. Re-run it once on the first progress update. Apple is always treated as HLS.
    if (PlatformUtil.isApple() || this.isHlsPlayback_()) {
      this.player_.subscribe('progressupdate', this.onProgressUpdate);
    }
    if (!PlatformUtil.isApple()) {
      this.player_.subscribe('manifestupdated', this.onManifestUpdated);
    }
  }

  private unsubscribeToPlayerEvents(): void {
    if (!this.player_) {
      return;
    }

    const baseEvents = [
      { event: 'playbackstatechanged', handler: this.onPlaybackStateChanged },
      { event: 'streamended', handler: this.onStreamEnded },
      { event: 'manifestparsed', handler: this.onManifestParsed },
      { event: 'seekingstatechanged', handler: this.onSeekingStateChanged },
      { event: 'error', handler: this.onError },
    ];

    baseEvents.forEach(({ event, handler }) => this.player_.unsubscribe(event, handler));
    if (PlatformUtil.isApple() || this.isHlsPlayback_()) {
      this.player_.unsubscribe('progressupdate', this.onProgressUpdate);
    }

    if (!PlatformUtil.isApple()) {
      this.player_.unsubscribe('manifestupdated', this.onManifestUpdated);
    }
  }
}
